import React, { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Icon, Link } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import { getSplitTestValue } from '@src/utils/selectors';

import messages from './BusinessBankStatementTrueLayer.messages';
import TrueLayerModal, { TTrueLayerBank } from './TrueLayerModal';
import {
    ACTIVATE_TRUELAYER,
    FETCH_TRUELAYER_URL
} from '../../../../components/TrueLayer/TrueLayer.actions';
import {
    getTrueLayerRedirectURL,
    getUrlRequestErrorStatus,
    isFetchingTrueLayerURL
} from '../../../../components/TrueLayer/TrueLayer.selectors';
import { getCookie } from '../../../../helpers/helpers';
import {
    getCountryCode,
    isRBLApplication
} from '../../../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../../../tracking/amplitudeEvents';
import styles from '../BusinessBankStatement.module.css';
import { useTrueLayerRedirected } from '../hooks/useTrueLayerRedirected';

type PropsFromRedux = ConnectedProps<typeof connectorHOC>;
type Props = PropsFromRedux & {
    setAlertStatus: (status: 'success' | 'failure' | 'cancel' | null) => void;
    bankList: TTrueLayerBank[];
};

const mapStateToProps = (state: any) => ({
    country: getCountryCode(state),
    isRBLApplication: isRBLApplication(state),
    isFetching: isFetchingTrueLayerURL(state),
    trueLayerRedirectURL: getTrueLayerRedirectURL(state),
    urlRequestHasError: getUrlRequestErrorStatus(state),
    isInTrueLayerConsentFlow: getSplitTestValue(
        state,
        'new_truelayer_consent_flow'
    ),
    isInSplitOpenBankingPreference: getSplitTestValue(
        state,
        'open_banking_preference'
    )
});

const mapDispatchToProps = {
    activateTruelayer: () => ({
        type: ACTIVATE_TRUELAYER
    }),
    fetchTruelayerURL: () => ({
        type: FETCH_TRUELAYER_URL
    })
};

const BusinessBankStatementTrueLayer = ({
    bankList,
    country,
    activateTruelayer,
    fetchTruelayerURL,
    trueLayerRedirectURL,
    isFetching,
    setAlertStatus,
    isInTrueLayerConsentFlow,
    isInSplitOpenBankingPreference
}: Props) => {
    const trueLayerModalCookie = getCookie('new_true_layer_modal');
    const [trueLayerModal, setTrueLayerModal] = useState(false);
    const { alertStatus } = useTrueLayerRedirected();

    useEffect(() => {
        activateTruelayer();
    }, [activateTruelayer]);

    const [trueLayerButtonLoading, setTrueLayerButtonLoading] =
        React.useState<boolean>(false);

    useEffect(() => {
        setAlertStatus(alertStatus);
    }, [alertStatus, setAlertStatus]);

    const handleCTAClick = async () => {
        if (isInTrueLayerConsentFlow || trueLayerModalCookie) {
            tracking.ampli.openBankingConnectStarted({
                ...getAmplitudeDefaultEventProperties()
            });

            return setTrueLayerModal(true);
        }

        // If Else required here due to Jest limitations with redirects
        if (trueLayerRedirectURL) {
            setTrueLayerButtonLoading(true);

            await tracking.ampli.openBankingConnectStarted({
                ...getAmplitudeDefaultEventProperties()
            }).promise;

            setTrueLayerButtonLoading(false);

            window.location.href = trueLayerRedirectURL;
        } else {
            fetchTruelayerURL();
        }
    };

    const handleTrueLayerClose = () => {
        tracking.ampli.trueLayerModalClosed();
        setTrueLayerModal(false);
    };

    return (
        <>
            <div className={styles.linkAccountInfoSection}>
                <div
                    className={styles.linkAccountInfoContent}
                    data-testid="bank-account">
                    <div
                        className={styles.descriptionContainer}
                        data-testid="open-banking-content">
                        <div className={styles.descriptionHeaderContainer}>
                            <h3>
                                <FormattedMessage {...messages.title} />
                            </h3>
                            {isInSplitOpenBankingPreference && (
                                <div className={styles.OBSubtitle}>
                                    <Icon
                                        icon="shieldCheck"
                                        className={styles.OBBankGradeIcon}
                                        data-testid="bank-grade-icon"
                                    />
                                    <p>Bank grade encryption</p>
                                </div>
                            )}
                        </div>
                        {country === 'uk' && (
                            <ul>
                                <li>
                                    This is a secure way to share your account
                                    information. Your credentials will not be
                                    shared with anyone else.{' '}
                                    <Link
                                        href="https://truelayer.com/openbanking/what-is-open-banking/"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        Learn more
                                    </Link>
                                </li>
                                <li>
                                    Your transactions must cover the stated
                                    period and be from the account(s) where the
                                    majority of your business revenue is
                                    generated.
                                </li>
                            </ul>
                        )}
                        {country === 'de' && (
                            <>
                                <p>
                                    Open Banking mit TrueLayer ist ein sicherer
                                    Weg, Ihre Kontodaten zu teilen, ohne Ihr
                                    Passwort weiterzugeben.{' '}
                                    <a
                                        href="https://www.iwoca.de/kredit/open-banking"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        Mehr erfahren
                                    </a>
                                </p>
                                <p>
                                    Verknüpfen Sie alle Konten, die mehr als 10%
                                    Ihres Gesamtumsatzes ausmachen.
                                </p>
                            </>
                        )}
                    </div>
                    <Button
                        className={styles.linkAccountCTA}
                        variant="secondary"
                        onClick={async () => await handleCTAClick()}
                        loading={isFetching || trueLayerButtonLoading}>
                        <FormattedMessage {...messages.cta} />{' '}
                        <Icon className={styles.linkIcon} icon="link" />
                    </Button>
                </div>
            </div>
            {(isInTrueLayerConsentFlow || trueLayerModalCookie) && (
                <TrueLayerModal
                    isOpen={trueLayerModal}
                    handleClose={handleTrueLayerClose}
                    bankList={bankList}
                />
            )}
        </>
    );
};

const connectorHOC = connect(mapStateToProps, mapDispatchToProps);

export default connectorHOC(BusinessBankStatementTrueLayer);
